import { useRef, useEffect } from "react";
import {
  RowSharedContent,
  formatAge,
  styles,
  type SharedProps,
} from "./_ItemRowShared.js";

export function ItemRowMobile({
  item,
  isRead,
  onRead,
  source,
  highlight,
}: SharedProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const skipBgRef = useRef<HTMLDivElement>(null);
  const touchState = useRef<{
    startX: number;
    startY: number;
    dir: "h" | "v" | null;
  }>({
    startX: 0,
    startY: 0,
    dir: null,
  });

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const THRESHOLD = 90;

    function startDrag(clientX: number, clientY: number) {
      touchState.current = { startX: clientX, startY: clientY, dir: null };
      const inner = innerRef.current;
      if (inner) inner.style.transition = "none";
    }

    function moveDrag(clientX: number, clientY: number, preventDefault?: () => void) {
      const dx = clientX - touchState.current.startX;
      const dy = clientY - touchState.current.startY;
      const s = touchState.current;
      if (s.dir === null) {
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
        s.dir = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      }
      if (s.dir !== "h") return;
      preventDefault?.();
      const inner = innerRef.current;
      if (!inner) return;
      inner.style.transform = `translateX(${dx}px)`;
      const skipBg = skipBgRef.current;
      if (skipBg) {
        skipBg.style.opacity = String(Math.min(1, Math.abs(dx) / THRESHOLD));
        skipBg.style.justifyContent = dx < 0 ? "flex-end" : "flex-start";
        skipBg.style.paddingLeft = dx > 0 ? "20px" : "0";
        skipBg.style.paddingRight = dx < 0 ? "20px" : "0";
      }
    }

    function endDrag() {
      const inner = innerRef.current;
      if (!inner || touchState.current.dir !== "h") return;
      const match = inner.style.transform.match(/-?[\d.]+/);
      const dx = match ? parseFloat(match[0]) : 0;
      inner.style.transition = "transform 0.25s ease";
      const skipBg = skipBgRef.current;
      if (Math.abs(dx) > THRESHOLD) {
        inner.style.transform = dx < 0 ? "translateX(-110%)" : "translateX(110%)";
        if (skipBg) { skipBg.style.transition = "opacity 0.25s ease"; skipBg.style.opacity = "0"; }
        setTimeout(() => onRead(item.id), 240);
      } else {
        inner.style.transform = "translateX(0)";
        if (skipBg) { skipBg.style.transition = "opacity 0.2s ease"; skipBg.style.opacity = "0"; }
      }
      touchState.current.dir = null;
    }

    // Touch
    function onTouchStart(e: TouchEvent) {
      const t = e.touches[0];
      if (!t) return;
      startDrag(t.clientX, t.clientY);
    }
    function onTouchMove(e: TouchEvent) {
      const t = e.touches[0];
      if (!t) return;
      moveDrag(t.clientX, t.clientY, () => e.preventDefault());
    }

    // Mouse
    function onMouseMove(e: MouseEvent) { moveDrag(e.clientX, e.clientY); }
    function onMouseUp() {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "";
      endDrag();
    }
    function onMouseDown(e: MouseEvent) {
      if ((e.target as HTMLElement).closest("a, button")) return;
      e.preventDefault();
      document.body.style.userSelect = "none";
      startDrag(e.clientX, e.clientY);
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    }

    row.addEventListener("touchstart", onTouchStart, { passive: true });
    row.addEventListener("touchmove", onTouchMove, { passive: false });
    row.addEventListener("touchend", endDrag, { passive: true });
    row.addEventListener("mousedown", onMouseDown);
    return () => {
      row.removeEventListener("touchstart", onTouchStart);
      row.removeEventListener("touchmove", onTouchMove);
      row.removeEventListener("touchend", endDrag);
      row.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "";
    };
  }, [item.id, onRead]);

  return (
    <div
      ref={rowRef}
      className={`${styles.row} ${styles.mobile} ${isRead ? styles.read : ""} ${source ? styles.hasSource : ""}`}
    >
      <div ref={skipBgRef} className={styles.swipeActionSkip}>
        <span className={styles.swipeLabel}>Skip</span>
      </div>
      <div ref={innerRef} className={styles.rowInner}>
        <RowSharedContent
          item={item}
          isRead={isRead}
          onRead={onRead}
          source={source}
          highlight={highlight}
          metaSlot={
            source ? (
              <div className={styles.mobileMeta}>
                <span className={styles.mobileSource}>{source.title}</span>
                <span className={styles.timeAgo}>
                  {formatAge(item.publishedAt)}
                </span>
                {item.author && (
                  <span className={styles.author}>{item.author}</span>
                )}
              </div>
            ) : undefined
          }
        />
      </div>
    </div>
  );
}
